"use client"

import { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { PictureInPicture2, Square } from "lucide-react"
import { cn } from "@/lib/utils"
import { MAX_RECORDING_SEC, type RecorderHandle } from "@/modules/feedback/lib/client/recorder"
import { CaptureHint, FloatingPill } from "./capture-overlay"

const fmt = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`

/** Document Picture-in-Picture (Chrome, Edge): отдельное окно поверх всех, в запись вкладки не попадает */
interface DocumentPip {
  requestWindow: (opts?: { width?: number; height?: number }) => Promise<Window>
  window: Window | null
}

const documentPip = (): DocumentPip | null => (typeof window !== "undefined" ? ((window as unknown as { documentPictureInPicture?: DocumentPip }).documentPictureInPicture ?? null) : null)

function copyStyles(target: Document) {
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const style = target.createElement("style")
      style.textContent = Array.from(sheet.cssRules)
        .map((r) => r.cssText)
        .join("\n")
      target.head.appendChild(style)
    } catch {
      if (!sheet.href) continue
      const link = target.createElement("link")
      link.rel = "stylesheet"
      link.href = sheet.href
      target.head.appendChild(link)
    }
  }
  target.documentElement.className = document.documentElement.className
}

/** Индикатор записи как в iPhone: время и «стоп» в маленькой полупрозрачной кнопке вверху экрана */
export function RecordingIndicator({ recorder }: { recorder: RecorderHandle }) {
  const [elapsed, setElapsed] = useState(0)
  const [pip, setPip] = useState<Window | null>(null)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const pipSupported = recorder.mode === "display" && !!documentPip()

  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - recorder.startedAt) / 1000)), 250)
    return () => clearInterval(t)
  }, [recorder])

  const openPip = useCallback(async () => {
    const api = documentPip()
    if (!api) return
    try {
      const w = await api.requestWindow({ width: 220, height: 56 })
      copyStyles(w.document)
      w.document.title = "Запись экрана"
      w.document.body.style.cssText = "margin:0;display:flex;align-items:center;justify-content:center;height:100vh;background:#0f172a"
      w.addEventListener("pagehide", () => setPip(null), { once: true })
      setPip(w)
    } catch {
      // окно не открылось (нет жеста пользователя) — индикатор остаётся на странице
    }
  }, [])

  useEffect(() => () => pip?.close(), [pip])

  const ending = MAX_RECORDING_SEC - elapsed <= 60
  const time = (
    <span className={cn("flex items-center gap-1.5 pl-1.5 pr-0.5 font-mono text-xs font-medium tabular-nums", ending && "text-amber-300")} data-testid="recording-time">
      <span className="h-2 w-2 animate-pulse rounded-full bg-rose-500" />
      {fmt(elapsed)}
    </span>
  )
  const stop = (
    <button type="button" onClick={() => recorder.stop()} className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-600 text-white" aria-label="Остановить запись" title="Остановить запись" data-testid="recording-stop">
      <Square className="h-2.5 w-2.5 fill-current" />
    </button>
  )

  if (pip) {
    return createPortal(
      <div className="flex items-center gap-2 text-white" data-testid="recording-indicator" role="status">
        {time}
        {stop}
      </div>,
      pip.document.body,
    )
  }

  return (
    <>
      <FloatingPill className="group" testId="recording-indicator" title={recorder.mode === "dom" ? "Индикатор не попадает в запись. Перетащите, чтобы не мешал" : "Перетащите, чтобы не мешал"} onRect={setAnchor}>
        {time}
        {pipSupported && (
          <button
            type="button"
            className="hidden h-6 w-6 items-center justify-center rounded-full text-white/70 hover:text-white group-hover:flex"
            onClick={() => void openPip()}
            title="Вынести в отдельное окно — индикатор не попадёт в запись вкладки или окна"
            aria-label="Вынести индикатор в отдельное окно"
            data-testid="recording-pip"
          >
            <PictureInPicture2 className="h-3.5 w-3.5" />
          </button>
        )}
        {stop}
      </FloatingPill>
      {anchor && <CaptureHint text="Идёт запись. Чтобы остановить, нажмите на эту кнопку" anchor={anchor} />}
    </>
  )
}
